import './ProviderDetail.css';

import AvailabilityCalendar, { buildAvailabilitySlots } from './AvailabilityCalendar';
import ProviderWorkQueuePanel, { summarizeWorkItems } from './workQueue/ProviderWorkQueuePanel';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import JsonView from '@uiw/react-json-view';
import { Provider } from '../types/provider.types';
import StepGuidance from './StepGuidance';
import apiService from '../services/api.service';
import { padEpicId } from '../utils/epic-id.utils';
import { useProviderWorkItems } from './workQueue/useProviderWorkItems';
import { useWorkQueueLists } from './workQueue/workQueueLists';

interface InsuranceTreeNodeProps {
  carrier: string;
  products: string[];
}

const InsuranceTreeNode: React.FC<InsuranceTreeNodeProps> = ({ carrier, products }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="insurance-tree-node">
      <div
        className="insurance-carrier"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="tree-icon">{isExpanded ? '▼' : '▶'}</span>
        <strong>{carrier}</strong>
        <span className="product-count">({products.length})</span>
      </div>
      {isExpanded && products.length > 0 && (
        <div className="insurance-products">
          {products.map((product, idx) => (
            <div key={idx} className="insurance-product">
              {product}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PeriodAttempt {
  periodOffset: number;
  dateRange: {
    start: string;
    end: string;
  };
  hasSlots: boolean;
  slotCount: number;
  status: 'checking' | 'success' | 'no-slots' | 'error';
  errorMessage?: string;
}

interface ValidationResult {
  attempts: PeriodAttempt[];
  hasSlots: boolean;
  canContinue: boolean;
  slotsData: Array<{
    date: string;
    departmentId: string;
    visitTypeId: string;
    visitTypeName: string;
    visitTypeInternalName: string;
    slotCount: number;
  }>;
  appointmentData?: any; // Store the full appointment data for VisitTypes lookup
}

interface LocationDepartmentStaging {
  Npi: string;
  ProviderName: string | null;
  ActiveSchedulingLocation: boolean | null;
  OpenSchedulingActive: boolean | null;
  Location: string | null;
  DepartmentId: string | null;
  Department: string | null;
}

const ProviderDetail: React.FC = () => {
  const { npi } = useParams<{ npi: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [visitTypeValidation, setVisitTypeValidation] = useState<{
    validVisitTypes: any[];
    invalidVisitTypes: string[];
    missingVisitTypes: string[];
    status: 'pending' | 'checking' | 'valid' | 'invalid' | 'connection-failed';
    errorMessage?: string;
    attemptedVisitTypeIds?: string[];
  } | null>(null);
  const [visitTypeReasonValidation, setVisitTypeReasonValidation] = useState<{
    validMappings: any[];
    missingVisitTypes: string[];
    status: 'pending' | 'checking' | 'valid' | 'invalid' | 'connection-failed';
    errorMessage?: string;
    attemptedVisitTypeIds?: string[];
  } | null>(null);
  const [epicExtractData, setEpicExtractData] = useState<LocationDepartmentStaging[] | null>(null);
  const [epicExtractLoading, setEpicExtractLoading] = useState(false);
  const [epicExtractError, setEpicExtractError] = useState<string | null>(null);
  const [showValidationResponse, setShowValidationResponse] = useState(false);
  const [epicBlockSnapshot, setEpicBlockSnapshot] = useState<any | null>(null);
  const [recheckPending, setRecheckPending] = useState(false);
  const [recheckError, setRecheckError] = useState<string | null>(null);
  const [epicBlockLoading, setEpicBlockLoading] = useState(false);
  const [epicBlockChecked, setEpicBlockChecked] = useState(false);
  const requestAbortRef = useRef<AbortController | null>(null);
  const headerSectionRef = useRef<HTMLDivElement>(null);
  const workItems = useProviderWorkItems(npi);
  const workQueueLists = useWorkQueueLists();
  // null = not chosen yet: opens itself when there is open work to act on.
  const [workQueueChoice, setWorkQueueChoice] = useState<boolean | null>(null);
  const hasOpenWork = !!workItems.data && (
    (!!workItems.data.annotation && !workItems.data.annotation.addressed && !workItems.data.annotation.excludedFromUnresolved) ||
    workItems.data.departments.some((department) => department.annotation && !department.annotation.addressed)
  );
  const workQueueOpen = workQueueChoice ?? hasOpenWork;
  const showWorkQueue = () => {
    setWorkQueueChoice(true);
    requestAnimationFrame(() =>
      document.getElementById('work-queue-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  };
  const [splitPercent, setSplitPercent] = useState<number>(() => {
    const saved = Number(localStorage.getItem('providerValidator.detailSplitPercent'));
    return Number.isFinite(saved) && saved >= 20 && saved <= 70 ? saved : 38;
  });

  const startColumnResize = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const container = headerSectionRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    const handle = event.currentTarget;
    handle.classList.add('column-resizer-active');

    const onMove = (move: MouseEvent) => {
      const percent = ((move.clientX - bounds.left) / bounds.width) * 100;
      setSplitPercent(Math.min(70, Math.max(20, percent)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      handle.classList.remove('column-resizer-active');
      setSplitPercent((current) => {
        localStorage.setItem('providerValidator.detailSplitPercent', String(Math.round(current)));
        return current;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Sorting state for Step 3 (Slots table)
  const [slotsSort, setSlotsSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Sorting state for Step 4 (Visit Type Mappings table)
  const [visitTypesSort, setVisitTypesSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Sorting state for Step 5 (Epic Extract table)
  const [epicExtractSort, setEpicExtractSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Sorting state for Step 6 (Visit Type Reason table)
  const [visitTypeReasonSort, setVisitTypeReasonSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  useEffect(() => {
    requestAbortRef.current?.abort();
    const abortController = new AbortController();
    requestAbortRef.current = abortController;

    if (npi) {
      loadProviderDetail(abortController.signal);
    } else {
      setError('No NPI provided');
      setLoading(false);
    }

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npi]);

  // Load Epic extract data only after Step 3 validation is complete
  useEffect(() => {
    if (validationResult && validationResult.hasSlots && npi) {
      loadEpicExtractData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationResult, npi]);

  // Snapshot data stands alone: it does not depend on the live validation steps.
  useEffect(() => {    if (!npi) return;
    let cancelled = false;

    (async () => {
      setEpicBlockLoading(true);
      try {
        const snapshot = await apiService.getEpicBlockSnapshot(npi);
        if (!cancelled) setEpicBlockSnapshot(snapshot);
      } catch (err: any) {
        if (!cancelled) setEpicBlockSnapshot(null);
      } finally {
        if (!cancelled) {
          setEpicBlockLoading(false);
          setEpicBlockChecked(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [npi]);

  /**
   * Re-evaluates the provider against Epic's scheduling rules right now. Rule-only,
   * so it returns in about a second; slot counts are left as the diagnostic found
   * them, and nothing is written back to the stored snapshot.
   */
  const recheckEpicBlocks = async () => {
    if (!npi) return;
    setRecheckPending(true);
    setRecheckError(null);
    try {
      const live = await apiService.recheckEpicBlocks(npi);
      // The capability flag rides on the stored snapshot, not the live response,
      // so carry it across or the control disappears after the first check.
      setEpicBlockSnapshot((previous: any) => ({
        ...live,
        liveRecheckAvailable: previous?.liveRecheckAvailable ?? true
      }));
    } catch (err: any) {
      setRecheckError(err?.response?.data?.error || 'Could not reach Epic for a live check.');
    } finally {
      setRecheckPending(false);
    }
  };

  const loadProviderDetail = async (signal?: AbortSignal) => {
    if (!npi) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getProviderDetail(npi, signal);
      if (signal?.aborted) return;
      setProvider(data);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      setError(err.message || 'Failed to load provider detail');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const loadEpicExtractData = async () => {
    if (!npi) return;
    const signal = requestAbortRef.current?.signal;
    if (!signal || signal.aborted) return;

    try {
      setEpicExtractLoading(true);
      setEpicExtractError(null);
      const data = await apiService.getEpicExtractData(npi, signal);
      if (signal.aborted) return;
      setEpicExtractData(data);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      console.error('Error loading Epic extract data:', err);
      setEpicExtractError(err.message || 'Failed to load Epic extract data');
    } finally {
      if (!signal.aborted) setEpicExtractLoading(false);
    }
  };

  // Automatically validate visit types when validation results are available
  useEffect(() => {
    if (validationResult && validationResult.slotsData.length > 0) {
      validateVisitTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationResult]);

  // Automatically validate visit type reasons after Step 4 completes
  useEffect(() => {
    if (visitTypeValidation && visitTypeValidation.status === 'valid' && visitTypeValidation.validVisitTypes.length > 0) {
      validateVisitTypeReasons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitTypeValidation]);

  // Sorting handlers
  const handleSlotsSort = (field: string) => {
    const newDirection = slotsSort?.field === field && slotsSort.direction === 'asc' ? 'desc' : 'asc';
    setSlotsSort({ field, direction: newDirection });
  };

  const handleVisitTypesSort = (field: string) => {
    const newDirection = visitTypesSort?.field === field && visitTypesSort.direction === 'asc' ? 'desc' : 'asc';
    setVisitTypesSort({ field, direction: newDirection });
  };

  const handleEpicExtractSort = (field: string) => {
    const newDirection = epicExtractSort?.field === field && epicExtractSort.direction === 'asc' ? 'desc' : 'asc';
    setEpicExtractSort({ field, direction: newDirection });
  };

  const handleVisitTypeReasonSort = (field: string) => {
    const newDirection = visitTypeReasonSort?.field === field && visitTypeReasonSort.direction === 'asc' ? 'desc' : 'asc';
    setVisitTypeReasonSort({ field, direction: newDirection });
  };

  const getSortIcon = (currentField: string, sortState: { field: string; direction: 'asc' | 'desc' } | null) => {
    if (!sortState || sortState.field !== currentField) return '';
    return sortState.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // `blocking` comes from the server-side reason dictionary, so healthy outcomes
  // like OK-BUT-NO-OPEN-SLOTS are not reported as configuration blocks.
  const blockingCombos: any[] = (epicBlockSnapshot?.combos || []).filter(
    (combo: any) => combo.reasonDetail?.blocking === true
  );

  // Departments that are never bookable by design; shown, but not actionable.
  const actionableCombos: any[] = blockingCombos.filter((combo: any) => !combo.departmentExcluded);
  const excludedComboCount = blockingCombos.length - actionableCombos.length;

  // Level 2. These pairings passed every configuration rule, so they never appear
  // as blocks above, and until the deep probe existed they all read as "full".
  // The probe splits them: some are genuinely capacity, the rest are Epic build
  // problems the cascade could not see.
  const deepCombos: any[] = (epicBlockSnapshot?.combos || []).filter((combo: any) => combo.deep?.reasonCode);
  const actionableDeepCombos: any[] = deepCombos.filter(
    (combo: any) => combo.deepReasonDetail?.blocking === true && !combo.departmentExcluded
  );
  const capacityDeepComboCount = deepCombos.length - actionableDeepCombos.length;

  // Matches the audit tab: blocks only on excluded departments are not blocks.
  const epicPreCheckPasses =
    !!epicBlockSnapshot &&
    (epicBlockSnapshot.status === 'HAS-SLOTS' ||
      (excludedComboCount > 0 && actionableCombos.length === 0 && epicBlockSnapshot.passCount > 0));

  // The workbook's status is the provider's best outcome, so a FULLY-BOOKED
  // provider can still hold blocked pairings; show the mix rather than one reason.
  const blockingReasonCounts: [string, number][] = (() => {
    const counts = new Map<string, number>();
    actionableCombos.forEach((combo: any) => {
      const label = combo.reasonDetail?.label || combo.reasonCode;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  })();

  /**
   * Epic department IDs on the provider's Marketing Master locations, from Symplr's
   * LocationDepartment (the map the queue tiers use). Location.EpicDepartmentId holds
   * one department per location and is often the wrong one, so it is only a fallback.
   */
  const symplrDepartmentIds = new Set<string>(
    Array.isArray(epicBlockSnapshot?.publishedDepartmentIds)
      ? (epicBlockSnapshot.publishedDepartmentIds as string[]).map((id) => padEpicId(id))
      : ((provider?.Locations || provider?.locations || []) as any[])
          .map((loc: any) => loc.EpicDepartmentId || loc.epicDepartmentId)
          .filter(Boolean)
          .flatMap((id: any) =>
            typeof id === 'string'
              ? id.split(',').map((part: string) => padEpicId(part.trim()))
              : [padEpicId(id)]
          )
          .filter((id: string) => id !== 'N/A')
  );

  /** Blocked departments with no Symplr mapping: they would never surface in #3. */
  const unmappedBlockedDepartments: string[] = Array.from(
    new Set(
      actionableCombos
        .map((combo: any) => combo.departmentId)
        .filter(Boolean)
        .filter((id: string) => !symplrDepartmentIds.has(padEpicId(id)))
    )
  );

  // Epic remediation grouped by reason code, limited to departments Symplr already
  // publishes: for unmapped departments the Epic fix alone changes nothing.
  const mappedBlockGuidance = (() => {
    const groups = new Map<string, { label: string; remediation: string; departments: Set<string> }>();
    actionableCombos.forEach((combo: any) => {
      const remediation = combo.reasonDetail?.remediation;
      if (!remediation) return;
      if (combo.departmentId && !symplrDepartmentIds.has(padEpicId(combo.departmentId))) return;
      const code = combo.reasonCode || 'UNKNOWN';
      let entry = groups.get(code);
      if (!entry) {
        entry = { label: combo.reasonDetail?.label || code, remediation, departments: new Set<string>() };
        groups.set(code, entry);
      }
      const department = combo.departmentName || (combo.departmentId ? padEpicId(combo.departmentId) : null);
      if (department) entry.departments.add(department);
    });
    return Array.from(groups.values()).map((group) => ({
      label: group.label,
      remediation: group.remediation,
      departments: Array.from(group.departments)
    }));
  })();

  // Sorted data functions
  const getSortedSlotsData = () => {
    if (!validationResult || !slotsSort) return validationResult?.slotsData || [];

    return [...validationResult.slotsData].sort((a, b) => {
      let aVal: any = (a as any)[slotsSort.field];
      let bVal: any = (b as any)[slotsSort.field];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return slotsSort.direction === 'asc' ? comparison : -comparison;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return slotsSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  };

  const getSortedVisitTypes = () => {
    if (!visitTypeValidation || !visitTypesSort) return visitTypeValidation?.validVisitTypes || [];

    return [...visitTypeValidation.validVisitTypes].sort((a, b) => {
      let aVal: any = (a as any)[visitTypesSort.field];
      let bVal: any = (b as any)[visitTypesSort.field];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return visitTypesSort.direction === 'asc' ? comparison : -comparison;
      }

      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return visitTypesSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      return 0;
    });
  };

  const getSortedEpicExtractData = () => {
    if (!epicExtractData || !epicExtractSort) return epicExtractData || [];

    return [...epicExtractData].sort((a, b) => {
      let aVal: any = (a as any)[epicExtractSort.field];
      let bVal: any = (b as any)[epicExtractSort.field];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return epicExtractSort.direction === 'asc' ? comparison : -comparison;
      }

      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return epicExtractSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      return 0;
    });
  };

  const getSortedVisitTypeReasons = () => {
    if (!visitTypeReasonValidation || !visitTypeReasonSort) return visitTypeReasonValidation?.validMappings || [];

    return [...visitTypeReasonValidation.validMappings].sort((a, b) => {
      let aVal: any = (a as any)[visitTypeReasonSort.field];
      let bVal: any = (b as any)[visitTypeReasonSort.field];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return visitTypeReasonSort.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });
  };

  const validateVisitTypes = async () => {
    if (!validationResult || validationResult.slotsData.length === 0) {
      return;
    }
    const signal = requestAbortRef.current?.signal;
    if (!signal || signal.aborted) return;

    // Extract unique visit type IDs from slots data
    const visitTypeIds = Array.from(new Set(validationResult.slotsData.map(slot => slot.visitTypeId)));

    try {
      setVisitTypeValidation({
        validVisitTypes: [],
        invalidVisitTypes: [],
        missingVisitTypes: [],
        status: 'checking'
      });

      const result = await apiService.validateVisitTypes(visitTypeIds, signal);
      if (signal.aborted) return;

      // Check if connection failed
      if (result.connectionFailed) {
        setVisitTypeValidation({
          validVisitTypes: [],
          invalidVisitTypes: [],
          missingVisitTypes: [],
          status: 'connection-failed',
          errorMessage: result.errorMessage,
          attemptedVisitTypeIds: visitTypeIds
        });
        return;
      }

      const hasInvalidOrMissing = result.invalidVisitTypes.length > 0 || result.missingVisitTypes.length > 0;

      setVisitTypeValidation({
        ...result,
        status: hasInvalidOrMissing ? 'invalid' : 'valid'
      });
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      // Extract detailed error message
      let detailedError = err.message || 'Failed to connect to validation service';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          detailedError += ` | Server response: ${err.response.data}`;
        } else if (err.response.data.message) {
          detailedError += ` | ${err.response.data.message}`;
        } else {
          detailedError += ` | ${JSON.stringify(err.response.data)}`;
        }
      }

      // Treat any API error (network error, 500, etc.) as connection failure
      setVisitTypeValidation({
        validVisitTypes: [],
        invalidVisitTypes: [],
        missingVisitTypes: [],
        status: 'connection-failed',
        errorMessage: detailedError,
        attemptedVisitTypeIds: visitTypeIds
      });
    }
  };

  const validateVisitTypeReasons = async () => {
    if (!validationResult || validationResult.slotsData.length === 0) {
      return;
    }
    const signal = requestAbortRef.current?.signal;
    if (!signal || signal.aborted) return;

    // Extract unique visit type IDs from slots data
    const visitTypeIds = Array.from(new Set(validationResult.slotsData.map(slot => slot.visitTypeId)));

    try {
      setVisitTypeReasonValidation({
        validMappings: [],
        missingVisitTypes: [],
        status: 'checking'
      });

      const result = await apiService.validateVisitTypeReasons(visitTypeIds, signal);
      if (signal.aborted) return;

      // Check if connection failed
      if (result.connectionFailed) {
        setVisitTypeReasonValidation({
          validMappings: [],
          missingVisitTypes: [],
          status: 'connection-failed',
          errorMessage: result.errorMessage,
          attemptedVisitTypeIds: visitTypeIds
        });
        return;
      }

      const hasMissing = result.missingVisitTypes.length > 0;

      setVisitTypeReasonValidation({
        ...result,
        status: hasMissing ? 'invalid' : 'valid'
      });
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      // Extract detailed error message
      let detailedError = err.message || 'Failed to connect to validation service';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          detailedError += ` | Server response: ${err.response.data}`;
        } else if (err.response.data.message) {
          detailedError += ` | ${err.response.data.message}`;
        } else {
          detailedError += ` | ${JSON.stringify(err.response.data)}`;
        }
      }

      // Treat any API error (network error, 500, etc.) as connection failure
      setVisitTypeReasonValidation({
        validMappings: [],
        missingVisitTypes: [],
        status: 'connection-failed',
        errorMessage: detailedError,
        attemptedVisitTypeIds: visitTypeIds
      });
    }
  };

  // Get visit type display name from VisitTypes array
  const getVisitTypeDisplayName = (visitTypeId: string, appointmentData: any): string => {
    if (!appointmentData) return 'N/A';

    const visitTypes = appointmentData.VisitTypes || [];

    // Find the visit type that has a matching ID in its IDs array
    for (const visitType of visitTypes) {
      const ids = visitType.IDs || [];
      const matchingId = ids.find((idObj: any) => idObj.ID === visitTypeId);
      if (matchingId) {
        return visitType.DisplayName || 'N/A';
      }
    }

    return 'N/A';
  };

  // Get visit type internal name from VisitTypes array
  const getVisitTypeInternalName = (visitTypeId: string, appointmentData: any): string => {
    if (!appointmentData) return 'N/A';

    const visitTypes = appointmentData.VisitTypes || [];

    // Find the visit type that has a matching ID in its IDs array
    for (const visitType of visitTypes) {
      const ids = visitType.IDs || [];
      const matchingId = ids.find((idObj: any) => idObj.ID === visitTypeId);
      if (matchingId) {
        return visitType.Name || 'N/A';
      }
    }

    return 'N/A';
  };

  const handleValidate = async (continueFromPeriod?: number) => {
    if (!npi) return;
    const signal = requestAbortRef.current?.signal;
    if (!signal || signal.aborted) return;

    try {
      setValidating(true);
      setShowValidationResponse(false);

      // Check if provider has Epic ID and pad it with leading zeroes
      const rawEpicId: string | undefined = provider?.EpicID || provider?.epicID;
      let epicId: string | undefined = rawEpicId ? padEpicId(rawEpicId) : undefined;

      // If no Epic ID, show error in validation results
      if (!epicId || epicId === 'N/A') {
        setValidationResult({
          attempts: [{
            periodOffset: 0,
            dateRange: { start: '', end: '' },
            hasSlots: false,
            slotCount: 0,
            status: 'error',
            errorMessage: 'Epic ID is required for validation. Please add Epic ID to provider data.'
          }],
          hasSlots: false,
          canContinue: false,
          slotsData: []
        });
        setValidating(false);
        return;
      }

      // Determine starting period offset
      const startPeriod = continueFromPeriod !== undefined ? continueFromPeriod : 0;
      const firstBatchMax = 6; // First batch: check up to 6 periods (0-5)
      const secondBatchMax = 12; // Second batch: check up to 12 periods (6-11)

      // Determine if we should auto-check multiple periods or just one
      // Auto-check when: initial validation OR continuing after checking all 6 periods with no slots
      const isInitialValidation = continueFromPeriod === undefined;
      const isContinuingAfterFirstBatch = startPeriod >= firstBatchMax && !validationResult?.hasSlots;
      const shouldAutoCheck = isInitialValidation || isContinuingAfterFirstBatch;

      // Calculate max period based on context
      let maxPeriod: number;
      if (shouldAutoCheck) {
        // Auto-check mode: check up to batch limit
        maxPeriod = startPeriod < firstBatchMax ? firstBatchMax : secondBatchMax;
      } else {
        // Manual mode (user clicked continue after finding slots): check just one period
        maxPeriod = startPeriod + 1;
      }

      // Preserve existing attempts if continuing
      const existingAttempts = continueFromPeriod !== undefined && validationResult ? validationResult.attempts : [];
      const attempts: PeriodAttempt[] = [...existingAttempts];
      let allSlotsData: Array<{ date: string; departmentId: string; visitTypeId: string; visitTypeName: string; visitTypeInternalName: string; slotCount: number }> = validationResult?.slotsData || [];
      let foundSlots = validationResult?.hasSlots || false;
      let storedAppointmentData = validationResult?.appointmentData || null;

      // Check periods until we find slots (if auto-checking) or reach max
      for (let periodOffset = startPeriod; periodOffset < maxPeriod; periodOffset++) {
        const periodLabel = periodOffset === 0 ? 'Next 30 days' : `Days ${periodOffset * 30}-${(periodOffset + 1) * 30}`;

        // Add attempt with "checking" status
        const currentAttempt: PeriodAttempt = {
          periodOffset,
          dateRange: { start: '', end: '' },
          hasSlots: false,
          slotCount: 0,
          status: 'checking'
        };
        attempts.push(currentAttempt);

        // Update UI to show checking status
        // Can continue if we haven't reached the absolute max (12 months)
        setValidationResult({
          attempts: [...attempts],
          hasSlots: foundSlots,
          canContinue: periodOffset < secondBatchMax - 1,
          slotsData: allSlotsData,
          appointmentData: storedAppointmentData
        });

        try {
          // Call validation API with period offset
          const response = await apiService.validateProvider(npi, epicId, periodOffset, signal);
          if (signal.aborted) return;

          // Store appointment data for VisitTypes lookup (use first response)
          if (!storedAppointmentData && response.appointmentData) {
            storedAppointmentData = response.appointmentData;
          }

          // Process slots data for display
          const daysWithSlots = response.appointmentData?.ScheduleDays?.filter((day: any) =>
            day.Slots && Array.isArray(day.Slots) && day.Slots.length > 0
          ) || [];

          // eslint-disable-next-line no-loop-func
          const periodSlotsData = daysWithSlots.map((day: any) => {
            const visitTypeId = day.VisitType?.ID || 'N/A';
            return {
              date: day.Date,
              departmentId: padEpicId(day.Department?.ID),
              visitTypeId: visitTypeId,
              visitTypeName: getVisitTypeDisplayName(visitTypeId, storedAppointmentData),
              visitTypeInternalName: getVisitTypeInternalName(visitTypeId, storedAppointmentData),
              slotCount: day.Slots.length
            };
          });

          // Update the current attempt with results
          const slotCount = daysWithSlots.length;
          currentAttempt.dateRange = response.dateRange;
          currentAttempt.hasSlots = response.hasSlots;
          currentAttempt.slotCount = slotCount;
          currentAttempt.status = response.hasSlots ? 'success' : 'no-slots';

          if (response.hasSlots) {
            foundSlots = true;
            allSlotsData = [...allSlotsData, ...periodSlotsData];
          }

          // Update UI with completed attempt
          setValidationResult({
            attempts: [...attempts],
            hasSlots: foundSlots,
            canContinue: periodOffset < secondBatchMax - 1,
            slotsData: allSlotsData,
            appointmentData: storedAppointmentData
          });

          // If we found slots AND we're in auto-check mode, stop checking
          // In manual mode (checking one at a time), we already stop because maxPeriod = startPeriod + 1
          if (foundSlots && shouldAutoCheck) {
            console.log(`✅ Found slots in period ${periodOffset} (${periodLabel})`);
            break;
          }
        } catch (err: any) {
          if (err?.code === 'ERR_CANCELED') return;
          console.error(`❌ Period ${periodOffset} check failed:`, err);
          currentAttempt.status = 'error';
          currentAttempt.errorMessage = err.message;

          // Update UI with error
          setValidationResult({
            attempts: [...attempts],
            hasSlots: foundSlots,
            canContinue: periodOffset < secondBatchMax - 1,
            slotsData: allSlotsData,
            appointmentData: storedAppointmentData
          });
        }
      }
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      console.error('❌ Validation failed:', err);
    } finally {
      if (!signal.aborted) setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="provider-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading provider detail...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="provider-detail">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="provider-detail">
        <div className="no-data">
          <p>Provider not found</p>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-detail">
      <div className="detail-header">
        <button onClick={() => navigate(-1)} className="btn btn-secondary">
          ← Back to Search
        </button>
        <button onClick={() => handleValidate()} disabled={validating} className="btn btn-primary">
          {validating ? 'Validating...' : 'Validate Provider'}
        </button>
      </div>

      <div className="detail-content">
        <div
          className="header-section"
          ref={headerSectionRef}
          style={{ ['--split-percent' as any]: `${splitPercent}%` }}
        >
          <div className="provider-info-column">
          <div className="provider-header-card">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              {(provider.Photo || provider.photo) && (
                <img src={provider.Photo || provider.photo} alt={provider.DisplayName || provider.displayName} className="provider-photo" />
              )}
              {(provider.FriendlyUrl || provider.friendlyUrl) && (
                <button
                  onClick={() => window.open(`https://www.bswhealth.com/${provider.FriendlyUrl || provider.friendlyUrl}`, '_blank')}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  View Public Profile
                </button>
              )}
            </div>
            <div className="provider-header-info">
              <h1>{provider.DisplayName || provider.displayName || provider.FullName || provider.fullName}</h1>
              {(provider.Degrees || provider.degrees) && (provider.Degrees || provider.degrees)!.length > 0 && (
                <p className="degrees">{(provider.Degrees || provider.degrees)!.map(d => d.Name || d.name || d.Code || d.code).join(', ')}</p>
              )}
              <div className="header-meta">
                <span><strong>NPI:</strong> {provider.NPI || provider.npi}</span>
                {(provider.EpicID || provider.epicID) && <span><strong>Epic ID:</strong> {padEpicId(provider.EpicID || provider.epicID)}</span>}
                <span><strong>Gender:</strong> {provider.ProviderGender || provider.providerGender || 'N/A'}</span>
                {(provider.ProviderType || provider.providerType) && <span><strong>Type:</strong> {provider.ProviderType || provider.providerType}</span>}
                <span>
                  <strong>Open Scheduling:</strong> {
                    (provider.IsOpenSchedulingAvailable !== undefined || provider.isOpenSchedulingAvailable !== undefined)
                      ? ((provider.IsOpenSchedulingAvailable || provider.isOpenSchedulingAvailable) ? 'Yes' : 'No')
                      : 'N/A'
                  }
                </span>
                <span>
                  <strong>Direct Scheduling:</strong> {
                    (provider.IsDirectSchedulingAvailable !== undefined || provider.isDirectSchedulingAvailable !== undefined)
                      ? ((provider.IsDirectSchedulingAvailable || provider.isDirectSchedulingAvailable) ? 'Yes' : 'No')
                      : 'N/A'
                  }
                </span>
                <span>
                  <strong>Is Referable:</strong> {
                    (provider.IsReferable !== undefined || provider.isReferable !== undefined)
                      ? ((provider.IsReferable || provider.isReferable) ? 'Yes' : 'No')
                      : 'N/A'
                  }
                </span>
                <span>
                  <strong>Primary Care:</strong> {
                    (provider.IsPrimaryCare !== undefined || provider.isPrimaryCare !== undefined)
                      ? ((provider.IsPrimaryCare || provider.isPrimaryCare) ? 'Yes' : 'No')
                      : 'N/A'
                  }
                </span>
                <span>
                  <strong>Specialty Care:</strong> {
                    (provider.IsSpecialtyCare !== undefined || provider.isSpecialtyCare !== undefined)
                      ? ((provider.IsSpecialtyCare || provider.isSpecialtyCare) ? 'Yes' : 'No')
                      : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          <div className="detail-grid provider-attribute-grid">
          {(provider.Specialties || provider.specialties) && (provider.Specialties || provider.specialties)!.length > 0 && (
            <div className="detail-card">
              <h3>Specialties</h3>
              <ul>
                {(provider.Specialties || provider.specialties)!.map((spec, idx) => (
                  <li key={idx}>
                    <strong>{spec.Specialty || spec.specialty || spec.Name || spec.name}</strong>
                    {(spec.IsBoardCertified || spec.isBoardCertified) && <span className="certified-badge">Board Certified</span>}
                    {(spec.IsPrimary || spec.isPrimary) && <span className="primary-badge">Primary</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(provider.BoardCertifications || provider.boardCertifications) && (provider.BoardCertifications || provider.boardCertifications)!.length > 0 && (
            <div className="detail-card">
              <h3>Board Certifications</h3>
              <ul>
                {(provider.BoardCertifications || provider.boardCertifications)!.map((cert, idx) => (
                  <li key={idx}>
                    <strong>{cert.BoardCertificationName || cert.boardCertificationName}</strong>
                    {(cert.BoardCertificationSpecialty || cert.boardCertificationSpecialty) && <span> - {cert.BoardCertificationSpecialty || cert.boardCertificationSpecialty}</span>}
                    {(cert.BoardCertificationYear || cert.boardCertificationYear) && <span> ({cert.BoardCertificationYear || cert.boardCertificationYear})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(provider.Languages || provider.languages) && (provider.Languages || provider.languages)!.length > 0 && (
            <div className="detail-card">
              <h3>Languages</h3>
              <div className="language-tags">
                {(provider.Languages || provider.languages)!.map((lang, idx) => (
                  <span key={idx} className="tag">
                    {lang.Language || lang.language}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(provider.practiceGroups || provider.PracticeGroups) && (provider.practiceGroups || provider.PracticeGroups)!.length > 0 && (
            <div className="detail-card">
              <h3>Practice Groups ({(provider.practiceGroups || provider.PracticeGroups)!.length})</h3>
              <ul>
                {(provider.practiceGroups || provider.PracticeGroups)!.map((group: any, idx: number) => {
                  // Debug: log the actual structure
                  if (idx === 0) {
                    console.log('Practice Group structure:', group);
                    console.log('Available keys:', Object.keys(group));
                  }
                  return (
                    <li key={idx}>
                      <strong>{group.practiceGroupName || group.PracticeGroupName || group.name || group.Name || JSON.stringify(group)}</strong>
                      {(group.practiceGroupId || group.PracticeGroupId || group.id || group.ID) && (
                        <span style={{ color: '#666', fontSize: '0.9em', marginLeft: '0.5rem' }}>
                          (ID: {group.practiceGroupId || group.PracticeGroupId || group.id || group.ID})
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          </div>

          {(provider.Biography || provider.biography) && (
            <div className="detail-card">
              <h3>Biography</h3>
              <div dangerouslySetInnerHTML={{ __html: provider.Biography || provider.biography || '' }} />
            </div>
          )}
          </div>

          <button
            type="button"
            className="column-resizer"
            aria-label="Resize provider and checklist columns"
            title="Drag to resize the columns"
            onMouseDown={startColumnResize}
          />

          <div className="validation-checklist-card">
            <h3>Open Scheduling Validation Checklist</h3>
            <div className="checklist-items">
              <div className={`checklist-item ${epicPreCheckPasses ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id="check-epic-blocks"
                  checked={epicPreCheckPasses}
                  disabled
                />
                <label htmlFor="check-epic-blocks">
                  <span className="item-title">Pre-check - Epic Open Scheduling configuration (daily diagnostic)</span>
                  <span className={`item-status ${
                    epicBlockLoading ? 'running' :
                    !epicBlockChecked ? 'pending' :
                    !epicBlockSnapshot ? 'pending' :
                    epicPreCheckPasses ? 'success' : 'error'
                  }`}>
                    {epicBlockLoading ? 'Checking...' :
                     !epicBlockSnapshot ? 'No snapshot' :
                     epicBlockSnapshot.status === 'HAS-SLOTS' ? 'Has slots' :
                     epicPreCheckPasses ? 'No actionable blocks' : epicBlockSnapshot.status}
                    <span className="info-tooltip">
                      <span className="info-icon">ⓘ</span>
                      <span className="tooltip-text">
                        This comes from the daily Epic Open Scheduling diagnostic, not a live call. It reports whether Epic configuration is suppressing this provider's availability.
                        <br/><br/>
                        <strong>The status is the provider's best outcome, not a summary.</strong> A provider marked FULLY-BOOKED or HAS-SLOTS can still hold blocked pairings: the status reflects their most successful department and visit type pairing, while the table below lists every pairing that failed. The line above breaks the pairings into slots returned, blocked, and configured but full.
                        <br/><br/>
                        Because it is a point-in-time snapshot, it can disagree with #2 above if Epic configuration changed after the snapshot was produced.
                        <br/><br/>
                        This differs from #3: that step only sees departments the live call returned slots for, so a department Epic blocks outright never appears there. The Symplr Mapping column below shows whether each blocked department is already assigned to a Marketing Master location.
                        <br/><br/>
                        <strong>Reading the Symplr Mapping column:</strong><br/>
                        - <strong>Blocked + Mapped</strong> - the department is already published to Symplr, so the remaining work is the Epic configuration named in the Reason column.<br/>
                        - <strong>Blocked + Not mapped</strong> - fixing Epic alone will not surface this department, because it is also missing a Marketing Master mapping. Confirm the department is meant to be publicly schedulable before requesting either change.
                      </span>
                    </span>
                  </span>
                  {workItems.data && (
                    <div className="wq-precheck-line">
                      <strong>Work queue:</strong> {summarizeWorkItems(workItems.data, workQueueLists)}{' '}
                      <button type="button" className="link-button" onClick={showWorkQueue}>
                        {workItems.data.annotation ? 'View notes' : 'Add a note'}
                      </button>
                    </div>
                  )}
                  {epicBlockChecked && !epicBlockSnapshot && !epicBlockLoading && (
                    <div className="item-details">
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        This provider is not in the most recent Epic diagnostic snapshot.
                      </div>
                    </div>
                  )}
                  {epicBlockSnapshot && (
                    <div className="item-details">
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        <strong>
                          {epicBlockSnapshot.live
                            ? 'Checked live just now'
                            : `Snapshot ${epicBlockSnapshot.snapshotDate}`}
                        </strong>
                        {' — '}
                        {epicBlockSnapshot.comboCount} pairings: {epicBlockSnapshot.passCount} returning slots
                        {blockingCombos.length > 0 && `, ${blockingCombos.length} blocked`}
                        {epicBlockSnapshot.comboCount - epicBlockSnapshot.passCount - blockingCombos.length > 0 &&
                          `, ${epicBlockSnapshot.comboCount - epicBlockSnapshot.passCount - blockingCombos.length} configured but returning no slots`}
                        .
                        {epicBlockSnapshot.liveRecheckAvailable && (
                          <button
                            type="button"
                            className="link-button"
                            style={{ marginLeft: '0.5rem' }}
                            disabled={recheckPending}
                            onClick={recheckEpicBlocks}
                          >
                            {recheckPending
                              ? 'Checking Epic...'
                              : epicBlockSnapshot.live
                                ? 'Check again'
                                : 'Check now'}
                          </button>
                        )}
                      </div>
                      {epicBlockSnapshot.live && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#605E5C' }}>
                          Configuration rules only. Slot counts still come from the last diagnostic run,
                          and this result is not saved to the snapshot.
                        </div>
                      )}
                      {recheckError && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#D13438' }}>
                          {recheckError}
                        </div>
                      )}
                      {blockingReasonCounts.length > 0 && !epicPreCheckPasses && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#D13438' }}>
                          {blockingReasonCounts.length === 1
                            ? actionableCombos[0]?.reasonDetail?.description
                            : blockingReasonCounts.map(([label, count]) => `${count} x ${label}`).join('  ·  ')}
                        </div>
                      )}
                      {(mappedBlockGuidance.length > 0 || unmappedBlockedDepartments.length > 0) &&
                        !epicPreCheckPasses && (
                        <StepGuidance>
                          {mappedBlockGuidance.length === 0
                            ? 'No Epic remediation is listed for the reasons blocking this provider.'
                            : mappedBlockGuidance.length === 1
                              ? 'Fix this in Epic for the departments Symplr already publishes:'
                              : 'Each reason below is a separate fix in Epic, for the departments Symplr already publishes:'}
                          <ul>
                            {mappedBlockGuidance.map((group) => (
                              <li key={group.label}>
                                <strong>{group.label}</strong> - {group.remediation}
                                {group.departments.length > 0 && (
                                  <>
                                    <br />
                                    <small>{group.departments.join(', ')}</small>
                                  </>
                                )}
                              </li>
                            ))}
                            {unmappedBlockedDepartments.length > 0 && (
                              <li>
                                Departments marked <strong>Not mapped</strong> below have no Marketing Master
                                location in Symplr. Request one only if the department is meant to be publicly
                                bookable - otherwise the Epic block is the correct outcome and no action is needed.
                              </li>
                            )}
                          </ul>
                        </StepGuidance>
                      )}
                      {blockingCombos.length > 0 && (
                        <>
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Blocked pairings ({blockingCombos.length}):</strong>
                            {epicBlockSnapshot.status === 'HAS-SLOTS' && (
                              <span style={{ marginLeft: '0.5rem', color: '#666', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                this provider has slots elsewhere, but these department and visit type pairings are blocked
                              </span>
                            )}
                          </div>
                          {excludedComboCount > 0 && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                              {excludedComboCount} of these {excludedComboCount === 1 ? 'is' : 'are'} on a department that is
                              never bookable by design, marked <strong>Not schedulable</strong> below and left out of the guidance above.
                            </div>
                          )}
                          {unmappedBlockedDepartments.length > 0 && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#D13438' }}>
                              {unmappedBlockedDepartments.length} blocked department
                              {unmappedBlockedDepartments.length === 1 ? ' is' : 's are'} not mapped to a Marketing Master
                              location in Symplr: {unmappedBlockedDepartments.join(', ')}
                              <div style={{ color: '#666', fontStyle: 'italic', marginTop: '0.2rem' }}>
                                Correcting the Epic configuration alone will not surface
                                {unmappedBlockedDepartments.length === 1 ? ' this department' : ' these departments'};
                                the Symplr mapping is missing too.
                              </div>
                            </div>
                          )}
                          <div className="slots-table-wrapper">
                            <table className="results-table validation-results-table">
                              <thead>
                                <tr>
                                  <th>Department</th>
                                  <th>Symplr Mapping</th>
                                  <th>Visit Type</th>
                                  <th>Failing Layers</th>
                                  <th>Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {blockingCombos.map((combo: any, idx: number) => {
                                  const mapped = combo.departmentId
                                    ? symplrDepartmentIds.has(padEpicId(combo.departmentId))
                                    : null;
                                  const reasonTooltip = [
                                    combo.reasonDetail?.description,
                                    combo.reasonDetail?.epicReference &&
                                      `Epic reference: ${combo.reasonDetail.epicReference}`
                                  ]
                                    .filter(Boolean)
                                    .join('\n\n');
                                  return (
                                    <tr key={idx}>
                                      <td>{combo.departmentName || 'N/A'}<br/><small>{combo.departmentId || ''}</small></td>
                                      <td>
                                        {mapped === null ? (
                                          'N/A'
                                        ) : mapped ? (
                                          <span style={{ color: '#107C10', fontWeight: 600 }}>Mapped</span>
                                        ) : (
                                          <span style={{ color: '#D13438', fontWeight: 600 }}>Not mapped</span>
                                        )}
                                        {combo.departmentExcluded && (
                                          <>
                                            <br />
                                            <small style={{ color: '#666', fontStyle: 'italic' }}>Not schedulable</small>
                                          </>
                                        )}
                                      </td>
                                      <td>{combo.visitTypeName || 'N/A'}<br/><small>{combo.visitTypeId || ''}</small></td>
                                      <td>{combo.failingLayers?.length ? combo.failingLayers.join(', ') : 'N/A'}</td>
                                      <td className="reason-cell" title={reasonTooltip}>
                                        {combo.reasonDetail?.label || combo.reasonCode}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                      {deepCombos.length > 0 && (
                        <>
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Deep findings ({deepCombos.length}):</strong>
                            <span style={{ marginLeft: '0.5rem', color: '#666', fontStyle: 'italic', fontSize: '0.85rem' }}>
                              these pairings passed every configuration rule and still returned no slots, so the
                              diagnostic re-probed them
                            </span>
                          </div>
                          <div
                            style={{
                              marginTop: '0.35rem',
                              fontSize: '0.85rem',
                              color: actionableDeepCombos.length > 0 ? '#D13438' : '#666'
                            }}
                          >
                            {actionableDeepCombos.length > 0
                              ? `${actionableDeepCombos.length} name an Epic build problem, not capacity.`
                              : 'None of these are Epic build problems.'}
                            {capacityDeepComboCount > 0 &&
                              ` ${capacityDeepComboCount} ${capacityDeepComboCount === 1 ? 'is' : 'are'} genuinely full, or bookable only past the 183-day window.`}
                          </div>
                          <div className="slots-table-wrapper">
                            <table className="results-table validation-results-table">
                              <thead>
                                <tr>
                                  <th>Department</th>
                                  <th>Visit Type</th>
                                  <th>Deep Finding</th>
                                  <th>Epic&apos;s message</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deepCombos.map((combo: any, idx: number) => {
                                  const detail = combo.deepReasonDetail;
                                  const deepTooltip = [
                                    detail?.description,
                                    detail?.remediation && `What to do: ${detail.remediation}`,
                                    detail?.owner && `Owner: ${detail.owner}`,
                                    detail?.test && `Test applied: ${detail.test}`
                                  ]
                                    .filter(Boolean)
                                    .join('\n\n');
                                  return (
                                    <tr key={`deep-${idx}`}>
                                      <td>{combo.departmentName || 'N/A'}<br/><small>{combo.departmentId || ''}</small></td>
                                      <td>{combo.visitTypeName || 'N/A'}<br/><small>{combo.visitTypeId || ''}</small></td>
                                      <td className="reason-cell" title={deepTooltip}>
                                        <span style={{ color: detail?.blocking ? '#D13438' : '#605E5C', fontWeight: 600 }}>
                                          {detail?.label || combo.deep.reasonCode}
                                        </span>
                                        {combo.deep.templateSlotsFound > 0 && (
                                          <>
                                            <br />
                                            <small>
                                              {combo.deep.templateSlotsFound} template slot(s)
                                              {combo.deep.firstTemplateSlotDate && `, first ${combo.deep.firstTemplateSlotDate}`}
                                            </small>
                                          </>
                                        )}
                                      </td>
                                      {/* Epic's own wording; it names the pool, block or template the fix has to touch. */}
                                      <td><small>{combo.deep.autoSchedErrMessage || combo.deep.openSchedErrDetails || 'N/A'}</small></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </label>
              </div>
              {(() => {
                const isOpenScheduling = provider.IsOpenSchedulingAvailable || provider.isOpenSchedulingAvailable;
                const isReferable = provider.IsReferable || provider.isReferable;
                const isPrimaryCare = provider.IsPrimaryCare || provider.isPrimaryCare;
                const isSpecialtyCare = provider.IsSpecialtyCare || provider.isSpecialtyCare;
                const hasCareType = isPrimaryCare === true || isSpecialtyCare === true;
                const symplrFlagsValid = isOpenScheduling === true && isReferable === true && hasCareType;

                return (
                  <div className={`checklist-item ${symplrFlagsValid ? 'checked' : ''}`}>
                    <input type="checkbox" id="check-symplr-flags" checked={symplrFlagsValid} disabled />
                    <label htmlFor="check-symplr-flags">
                      <span className="item-title">#1 - Provider Flags in Symplr</span>
                      <span className={`item-status ${symplrFlagsValid ? 'success' : 'error'}`}>
                        {symplrFlagsValid ? 'Valid' : 'Invalid'}
                        <span className="info-tooltip">
                          <span className="info-icon">ⓘ</span>
                          <span className="tooltip-text">
                            To show a "Schedule Online" button for this provider, they must be Referable, have their IsOpenSchedulingAvailable flag set in Symplr, and be marked as either Primary Care or Specialty Care (or both).
                            <br/><br/>This step can also fail if the provider is not marked as either a Primary Care provider or a Specialty Care provider.
                            <br/><br/>
                            The IsOpenSchedulingAvailable flag comes from the Epic extract file. If there is a mistake in the Referable status of a provider, please open a Service Now Request (RITM) and have it assigned to the <strong>BSWH AG Enterprise Directory</strong> team.
                          </span>
                        </span>
                      </span>
                    </label>
                    {!symplrFlagsValid && (
                      <StepGuidance>
                        Correct the failing flags in Symplr. A provider needs <strong>IsReferable</strong>, <strong>IsOpenSchedulingAvailable</strong>, and at least one of Primary Care or Specialty Care.
                        <ul>
                          <li>Referable or care-type errors: open a ServiceNow Request (RITM) assigned to <strong>BSWH AG Enterprise Directory</strong>.</li>
                          <li>IsOpenSchedulingAvailable comes from the daily Epic extract, so an Epic-side change lands here the next day rather than immediately.</li>
                        </ul>
                      </StepGuidance>
                    )}
                  </div>
                );
              })()}
              <div className={`checklist-item ${validationResult?.hasSlots ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id="check-schedule-slots"
                  checked={validationResult?.hasSlots || false}
                  disabled
                />
                <label htmlFor="check-schedule-slots">
                  <span className="item-title">#2 - Schedule slots visible to Epic APIs (Live Epic API call)</span>
                  <span className={`item-status ${validationResult ? (validationResult.hasSlots ? 'success' : 'error') : 'pending'}`}>
                    {validationResult ? (validationResult.hasSlots ? 'Valid' : 'No Slots') : validating ? 'Checking...' : 'Pending'}
                    <span className="info-tooltip">
                      <span className="info-icon">ⓘ</span>
                      <span className="tooltip-text">
                        Click the "Validate Provider" button to make a live call to Epic to see what scheduling slots return.
                        <br/><br/>
                        If all Epic settings are correct, we should see slots returning below. The slots should be matched to the Epic Department IDs mapped to the Marketing Master locations.
                        <br/><br/>
                        This validation will look out 6 months for available slots.
                      </span>
                    </span>
                  </span>
                  {validationResult && (
                    <div className="item-details">
                      {/* Display all period attempts */}
                      {validationResult.attempts.length > 0 && (
                        <div className="validation-attempts">
                          <strong>Checked Periods:</strong>
                          <table className="validation-attempts-table">
                            <thead>
                              <tr>
                                <th>Period</th>
                                <th>Date Range</th>
                                <th>Status</th>
                                <th>Slots Found</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validationResult.attempts.map((attempt, idx) => (
                                <tr key={idx} className={`attempt-${attempt.status}`}>
                                  <td>
                                    {attempt.periodOffset === 0 ? 'Next 30 days' : `Days ${attempt.periodOffset * 30}-${(attempt.periodOffset + 1) * 30}`}
                                  </td>
                                  <td>
                                    {attempt.dateRange.start && attempt.dateRange.end
                                      ? `${attempt.dateRange.start} to ${attempt.dateRange.end}`
                                      : '-'}
                                  </td>
                                  <td>
                                    {attempt.status === 'checking' && <span className="status-badge checking">Checking...</span>}
                                    {attempt.status === 'success' && <span className="status-badge success">✓ Found</span>}
                                    {attempt.status === 'no-slots' && <span className="status-badge no-slots">No Slots</span>}
                                    {attempt.status === 'error' && <span className="status-badge error">Error</span>}
                                  </td>
                                  <td className="slots-count">
                                    {attempt.status === 'success' ? attempt.slotCount : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {validationResult.attempts.some(a => a.status === 'error') && (
                            <div className="validation-error" style={{ marginTop: '0.5rem' }}>
                              {validationResult.attempts.find(a => a.errorMessage)?.errorMessage}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Display slots data if found */}
                      {validationResult.slotsData.length > 0 && (
                        <>
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Available Slots Details:</strong>
                          </div>
                          <div className="slots-table-wrapper">
                            <table className="results-table validation-results-table">
                              <thead>
                                <tr>
                                  <th className="sortable" onClick={() => handleSlotsSort('date')}>
                                    Date{getSortIcon('date', slotsSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleSlotsSort('departmentId')}>
                                    Department ID{getSortIcon('departmentId', slotsSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleSlotsSort('visitTypeId')}>
                                    Visit Type ID{getSortIcon('visitTypeId', slotsSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleSlotsSort('visitTypeName')}>
                                    VT-Name{getSortIcon('visitTypeName', slotsSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleSlotsSort('visitTypeInternalName')}>
                                    VT-Epic Internal{getSortIcon('visitTypeInternalName', slotsSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleSlotsSort('slotCount')}>
                                    Slot Count{getSortIcon('slotCount', slotsSort)}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSortedSlotsData().map((slot, idx) => (
                                  <tr key={idx}>
                                    <td>{slot.date}</td>
                                    <td>{slot.departmentId}</td>
                                    <td>{slot.visitTypeId}</td>
                                    <td>{slot.visitTypeName}</td>
                                    <td>{slot.visitTypeInternalName}</td>
                                    <td>{slot.slotCount}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {/* Continue button if we can check more periods */}
                      {!validating && validationResult.canContinue && (
                        <div className="validation-actions" style={{ marginTop: '1rem' }}>
                          <button
                            onClick={() => {
                              const lastPeriod = validationResult.attempts[validationResult.attempts.length - 1]?.periodOffset || 0;
                              handleValidate(lastPeriod + 1);
                            }}
                            disabled={validating}
                            className="btn btn-secondary btn-sm"
                          >
                            {validationResult.hasSlots
                              ? 'Continue Checking (Next 30 days)'
                              : validationResult.attempts.length >= 6
                                ? 'Continue Checking (Next 6 months)'
                                : 'Continue Checking (Next 30 days)'}
                          </button>
                          <span className="validation-hint">
                            {validationResult.hasSlots
                              ? 'Check additional periods one at a time'
                              : validationResult.attempts.length >= 6
                                ? 'Check the next 6 months automatically'
                                : 'Check additional months for availability'}
                          </span>
                        </div>
                      )}

                      {/* Max reached message */}
                      {!validationResult.canContinue && validationResult.attempts.length > 0 && !validationResult.attempts.some(a => a.status === 'error') && (
                        <div className="validation-max-reached" style={{ marginTop: '1rem' }}>
                          {validationResult.hasSlots
                            ? 'Checked all available periods (up to 12 months)'
                            : 'Checked all 12 months - no slots found'}
                        </div>
                      )}

                      {!validationResult.hasSlots && (
                        <StepGuidance>
                          Epic returned no bookable slots. Use the Pre-check above to tell configuration from capacity:
                          <ul>
                            <li>If the Pre-check lists blocked pairings, that is the cause. Fix the Epic configuration named in its Reason column.</li>
                            <li>If the Pre-check shows no blocks, the schedule is more likely fully booked or missing a future template, which is a scheduling and template question rather than a configuration one.</li>
                          </ul>
                        </StepGuidance>
                      )}
                    </div>
                  )}
                </label>
              </div>
              {(() => {
                const departmentIds = Array.from(symplrDepartmentIds);

                // Check if all Department IDs from validation results are in the provider's department list
                const hasValidationResults = validationResult && validationResult.slotsData.length > 0;
                const deptIdsFromValidation = hasValidationResults
                  ? validationResult.slotsData.map(slot => slot.departmentId).filter(id => id !== 'N/A')
                  : [];

                const uniqueDeptIdsFromValidation = Array.from(new Set(deptIdsFromValidation));

                // Check if all department IDs from validation are present in provider's department IDs (including CSV values)
                const allDeptIdsMatch = hasValidationResults && uniqueDeptIdsFromValidation.length > 0
                  ? uniqueDeptIdsFromValidation.every(deptId => departmentIds.includes(deptId))
                  : false;

                const checkPassed = allDeptIdsMatch;

                return (
                  <div className={`checklist-item ${checkPassed ? 'checked' : ''}`}>
                    <input type="checkbox" id="check-dept-ids" checked={checkPassed} disabled />
                    <label htmlFor="check-dept-ids">
                      <span className="item-title">#3 - Department IDs added to Marketing Master Locations in Symplr</span>
                      <span className={`item-status ${checkPassed ? 'success' : 'pending'}`}>
                        {checkPassed ? 'Valid' : 'Pending'}
                        <span className="info-tooltip">
                          <span className="info-icon">ⓘ</span>
                          <span className="tooltip-text">
                            Epic Department IDs associated with scheduling must be assigned to a Marketing Master location in Symplr. The assignment is based on the daily Epic extract loaded to Symplr.
                            <br/><br/>
                            <strong>Extract timing:</strong><br/>
                            - Epic generates a file and drops to SFTP site at ~10am<br/>
                            - Symplr picks up the file and processes it in the afternoon<br/>
                            - Information is indexed overnight and made available via API the next day.
                          </span>
                        </span>
                      </span>
                      {departmentIds.length > 0 && (
                        <div className="item-details">
                          <strong>Epic Department IDs from Provider Locations:</strong>
                          <ul className="dept-id-list">
                            {departmentIds.map((id, idx) => (
                              <li key={idx}>{id}</li>
                            ))}
                          </ul>
                          {hasValidationResults && uniqueDeptIdsFromValidation.length > 0 && (
                            <>
                              <div style={{ marginTop: '1rem' }}>
                                <strong>Department IDs from Validation Results:</strong>
                              </div>
                              <ul className="dept-id-list">
                                {uniqueDeptIdsFromValidation.map((id, idx) => {
                                  const isPresent = departmentIds.includes(id);
                                  return (
                                    <li key={idx} style={{
                                      background: isPresent ? '#e8f5e9' : '#ffebee',
                                      borderLeftColor: isPresent ? '#4caf50' : '#f44336'
                                    }}>
                                      {id} {isPresent ? '✓' : '✗'}
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                    </label>
                    {hasValidationResults && !checkPassed && (
                      <StepGuidance>
                        Epic is returning slots for department IDs that are not on this provider's Marketing Master locations in Symplr, so those slots will not surface publicly.
                        <ul>
                          <li>This mapping arrives via the daily extract (Epic drops the file around 10am, Symplr processes it that afternoon, and it is indexed overnight), so a recent Epic change may simply need another cycle.</li>
                          <li>If the department is still unmapped after the next cycle, request the Marketing Master location assignment through the same ServiceNow route used in #1 (<strong>BSWH AG Enterprise Directory</strong>).</li>
                        </ul>
                      </StepGuidance>
                    )}
                  </div>
                );
              })()}
              <div className={`checklist-item ${visitTypeValidation?.status === 'valid' ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id="check-visit-types"
                  checked={visitTypeValidation?.status === 'valid'}
                  disabled
                />
                <label htmlFor="check-visit-types">
                  <span className="item-title">#4 - Visit Type Mappings exist for available slots</span>
                  <span className={`item-status ${
                    !visitTypeValidation ? 'pending' :
                    visitTypeValidation.status === 'checking' ? 'running' :
                    visitTypeValidation.status === 'valid' ? 'success' :
                    visitTypeValidation.status === 'connection-failed' ? 'error' : 'error'
                  }`}>
                    {!visitTypeValidation ? 'Pending' :
                     visitTypeValidation.status === 'checking' ? 'Checking...' :
                     visitTypeValidation.status === 'valid' ? 'Valid' :
                     visitTypeValidation.status === 'connection-failed' ? 'CONNECTION FAILED' : 'Invalid'}
                    <span className="info-tooltip">
                      <span className="info-icon">ⓘ</span>
                      <span className="tooltip-text">
                        Visit Types must have a back-end mapping that indicates whether it accepts New or Existing patients. New visit types that have never been mapped needs to be reviewed by the Digital Scheduling team before they are released for use.
                      </span>
                    </span>
                  </span>
                  {visitTypeValidation && visitTypeValidation.status !== 'checking' && (
                    <div className="item-details">
                      {visitTypeValidation.status === 'connection-failed' ? (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#D13438' }}>
                            ❌ Unable to connect to database. This validation requires network access to Azure SQL Database on port 1433.
                          </div>
                          {visitTypeValidation.attemptedVisitTypeIds && visitTypeValidation.attemptedVisitTypeIds.length > 0 && (
                            <>
                              <div style={{ marginTop: '1rem' }}>
                                <strong>Visit Types That Should Have Been Tested ({visitTypeValidation.attemptedVisitTypeIds.length}):</strong>
                              </div>
                              <div className="slots-table-wrapper">
                                <table className="results-table validation-results-table">
                                  <thead>
                                    <tr>
                                      <th>Visit Type ID</th>
                                      <th>Friendly Display Name</th>
                                      <th>Epic Name</th>
                                      <th>Accepts</th>
                                      <th>Video Visit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visitTypeValidation.attemptedVisitTypeIds.map((vtId: string, idx: number) => {
                                      // Find the visit type name from validation result slots data
                                      const slotInfo = validationResult?.slotsData.find(slot => slot.visitTypeId === vtId);
                                      return (
                                        <tr key={idx} style={{ background: '#ffebee' }}>
                                          <td>{vtId}</td>
                                          <td>{slotInfo?.visitTypeName || 'N/A'}</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </>
                      ) : visitTypeValidation.status === 'invalid' ? (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                            {visitTypeValidation.missingVisitTypes.length > 0 && (
                              <div style={{ color: '#D13438' }}>
                                ❌ Missing in database: {visitTypeValidation.missingVisitTypes.join(', ')}
                              </div>
                            )}
                            {visitTypeValidation.invalidVisitTypes.length > 0 && (
                              <div style={{ color: '#D13438' }}>
                                ❌ Invalid (no Accepts value): {visitTypeValidation.invalidVisitTypes.join(', ')}
                              </div>
                            )}
                          </div>
                          {visitTypeValidation.validVisitTypes.length > 0 && (
                            <>
                              <div style={{ marginTop: '1rem' }}>
                                <strong>Visit Type Mappings:</strong>
                              </div>
                              <div className="slots-table-wrapper">
                                <table className="results-table validation-results-table">
                                  <thead>
                                    <tr>
                                      <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeId')}>
                                        Visit Type ID{getSortIcon('VisitTypeId', visitTypesSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeDisplayName')}>
                                        Friendly Name{getSortIcon('VisitTypeDisplayName', visitTypesSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeInternalName')}>
                                        Epic Name{getSortIcon('VisitTypeInternalName', visitTypesSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypesSort('Accepts')}>
                                        Accepts{getSortIcon('Accepts', visitTypesSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypesSort('isVideoVisit')}>
                                        Video Visit{getSortIcon('isVideoVisit', visitTypesSort)}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getSortedVisitTypes().map((vt: any, idx: number) => (
                                      <tr key={idx}>
                                        <td>{vt.VisitTypeId}</td>
                                        <td>{vt.VisitTypeDisplayName || 'N/A'}</td>
                                        <td>{vt.VisitTypeInternalName || 'N/A'}</td>
                                        <td>{vt.Accepts || 'N/A'}</td>
                                        <td>{vt.isVideoVisit ? 'Yes' : 'No'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#107C10' }}>
                            ✅ All {visitTypeValidation.validVisitTypes.length} visit types have valid mappings
                          </div>
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Visit Type Mappings:</strong>
                          </div>
                          <div className="slots-table-wrapper">
                            <table className="results-table validation-results-table">
                              <thead>
                                <tr>
                                  <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeId')}>
                                    Visit Type ID{getSortIcon('VisitTypeId', visitTypesSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeDisplayName')}>
                                    Friendly Name{getSortIcon('VisitTypeDisplayName', visitTypesSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypesSort('VisitTypeInternalName')}>
                                    Epic Name{getSortIcon('VisitTypeInternalName', visitTypesSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypesSort('Accepts')}>
                                    Accepts{getSortIcon('Accepts', visitTypesSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypesSort('isVideoVisit')}>
                                    Video Visit{getSortIcon('isVideoVisit', visitTypesSort)}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSortedVisitTypes().map((vt: any, idx: number) => (
                                  <tr key={idx}>
                                    <td>{vt.VisitTypeId}</td>
                                    <td>{vt.VisitTypeDisplayName || 'N/A'}</td>
                                    <td>{vt.VisitTypeInternalName || 'N/A'}</td>
                                    <td>{vt.Accepts || 'N/A'}</td>
                                    <td>{vt.isVideoVisit ? 'Yes' : 'No'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </label>
                {visitTypeValidation && visitTypeValidation.status === 'invalid' && (
                  <StepGuidance>
                    One or more visit types returned by Epic have no back-end mapping, so they cannot be offered to patients. New visit types must be reviewed by the <strong>Digital Scheduling</strong> team before release.
                    <ul>
                      <li>Send the unmapped visit type IDs listed above to Digital Scheduling to have the New/Existing patient mapping added.</li>
                    </ul>
                  </StepGuidance>
                )}
                {visitTypeValidation && visitTypeValidation.status === 'connection-failed' && (
                  <StepGuidance>
                    This check could not reach the database, so it is unknown rather than failed. It needs network access to Azure SQL on port 1433 - retry from a connected network before treating the visit types as unmapped.
                  </StepGuidance>
                )}
              </div>
              <div className={`checklist-item ${epicExtractData && epicExtractData.length > 0 ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id="check-epic-extract"
                  checked={epicExtractData !== null && epicExtractData.length > 0}
                  disabled
                />
                <label htmlFor="check-epic-extract">
                  <span className="item-title">#5 - Verify Epic extract data</span>
                  <span className={`item-status ${
                    epicExtractLoading ? 'running' :
                    epicExtractError ? 'error' :
                    epicExtractData && epicExtractData.length > 0 ? 'success' : 'pending'
                  }`}>
                    {epicExtractLoading ? 'Loading...' :
                     epicExtractError ? 'Error' :
                     epicExtractData && epicExtractData.length > 0 ? 'Data Available' : 'Pending'}
                  </span>
                  {epicExtractData && epicExtractData.length > 0 && (
                    <div className="item-details">
                      <div style={{ marginTop: '0.5rem' }}>
                        <strong>Epic Extract Data ({epicExtractData.length} record{epicExtractData.length !== 1 ? 's' : ''}):</strong>
                      </div>
                      <div className="slots-table-wrapper">
                        <table className="results-table validation-results-table">
                          <thead>
                            <tr>
                              <th className="sortable" onClick={() => handleEpicExtractSort('ProviderName')}>
                                Provider Name{getSortIcon('ProviderName', epicExtractSort)}
                              </th>
                              <th className="sortable" onClick={() => handleEpicExtractSort('ActiveSchedulingLocation')}>
                                Active Scheduling Location{getSortIcon('ActiveSchedulingLocation', epicExtractSort)}
                              </th>
                              <th className="sortable" onClick={() => handleEpicExtractSort('OpenSchedulingActive')}>
                                Open Scheduling Active{getSortIcon('OpenSchedulingActive', epicExtractSort)}
                              </th>
                              <th className="sortable" onClick={() => handleEpicExtractSort('Location')}>
                                Location{getSortIcon('Location', epicExtractSort)}
                              </th>
                              <th className="sortable" onClick={() => handleEpicExtractSort('DepartmentId')}>
                                Department ID{getSortIcon('DepartmentId', epicExtractSort)}
                              </th>
                              <th className="sortable" onClick={() => handleEpicExtractSort('Department')}>
                                Department{getSortIcon('Department', epicExtractSort)}
                              </th>
                              <th>Match Step 3</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSortedEpicExtractData().map((row, idx) => {
                              // Check if this DepartmentId exists in Step 3 slots data
                              const paddedDeptId = row.DepartmentId ? padEpicId(row.DepartmentId) : null;
                              const existsInStep3 = paddedDeptId && validationResult?.slotsData.some(slot =>
                                padEpicId(slot.departmentId) === paddedDeptId
                              );
                              return (
                                <tr key={idx} style={existsInStep3 ? {} : { background: '#fff3cd' }}>
                                  <td>{row.ProviderName || 'N/A'}</td>
                                  <td>{row.ActiveSchedulingLocation === null ? 'N/A' : row.ActiveSchedulingLocation ? 'Yes' : 'No'}</td>
                                  <td>{row.OpenSchedulingActive === null ? 'N/A' : row.OpenSchedulingActive ? 'Yes' : 'No'}</td>
                                  <td>{row.Location || 'N/A'}</td>
                                  <td>{paddedDeptId || 'N/A'}</td>
                                  <td>{row.Department || 'N/A'}</td>
                                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                                    {paddedDeptId ? (existsInStep3 ? '✅' : '❌') : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {validationResult && validationResult.slotsData.length > 0 && (
                        <div style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                          <strong>Comparison Summary:</strong>
                          <div style={{ marginTop: '0.5rem' }}>
                            {(() => {
                              const epicDeptIds = epicExtractData
                                .map(row => row.DepartmentId ? padEpicId(row.DepartmentId) : null)
                                .filter((id): id is string => id !== null);
                              const step3DeptIds = validationResult.slotsData
                                .map(slot => padEpicId(slot.departmentId));
                              const uniqueEpicIds = Array.from(new Set(epicDeptIds));
                              const uniqueStep3Ids = Array.from(new Set(step3DeptIds));
                              const matchingIds = uniqueEpicIds.filter(id => uniqueStep3Ids.includes(id));
                              const onlyInEpic = uniqueEpicIds.filter(id => !uniqueStep3Ids.includes(id));
                              const onlyInStep3 = uniqueStep3Ids.filter(id => !uniqueEpicIds.includes(id));

                              return (
                                <>
                                  <div style={{ color: '#107C10' }}>
                                    ✅ {matchingIds.length} Department ID{matchingIds.length !== 1 ? 's' : ''} match between Epic Extract and Step 3
                                    {matchingIds.length > 0 && `: ${matchingIds.join(', ')}`}
                                  </div>
                                  {onlyInEpic.length > 0 && (
                                    <div style={{ color: '#856404', marginTop: '0.5rem' }}>
                                      ⚠️ {onlyInEpic.length} Department ID{onlyInEpic.length !== 1 ? 's' : ''} only in Epic Extract: {onlyInEpic.join(', ')}
                                    </div>
                                  )}
                                  {onlyInStep3.length > 0 && (
                                    <div style={{ color: '#856404', marginTop: '0.5rem' }}>
                                      ⚠️ {onlyInStep3.length} Department ID{onlyInStep3.length !== 1 ? 's' : ''} only in Step 3: {onlyInStep3.join(', ')}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {epicExtractError && (
                    <div className="validation-error">
                      ❌ {epicExtractError}
                    </div>
                  )}
                </label>
                {!epicExtractLoading && !epicExtractError && validationResult?.hasSlots && (!epicExtractData || epicExtractData.length === 0) && (
                  <StepGuidance>
                    This provider has no rows in the Epic extract staging data, so Symplr has nothing to map their departments from.
                    <ul>
                      <li>The extract runs daily (Epic drops the file around 10am, Symplr processes it that afternoon, indexed overnight), so a newly configured provider may appear tomorrow.</li>
                      <li>If the provider stays absent across cycles, raise it with <strong>BSWH AG Enterprise Directory</strong> via ServiceNow, since the extract feeds the directory.</li>
                    </ul>
                  </StepGuidance>
                )}
              </div>
              <div className={`checklist-item ${visitTypeReasonValidation?.status === 'valid' ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  id="check-visit-type-reasons"
                  checked={visitTypeReasonValidation?.status === 'valid'}
                  disabled
                />
                <label htmlFor="check-visit-type-reasons">
                  <span className="item-title">#6 - Visit Type Reason Mapping exists</span>
                  <span className={`item-status ${
                    !visitTypeReasonValidation ? 'pending' :
                    visitTypeReasonValidation.status === 'checking' ? 'running' :
                    visitTypeReasonValidation.status === 'valid' ? 'success' :
                    visitTypeReasonValidation.status === 'connection-failed' ? 'error' : 'error'
                  }`}>
                    {!visitTypeReasonValidation ? 'Pending' :
                     visitTypeReasonValidation.status === 'checking' ? 'Checking...' :
                     visitTypeReasonValidation.status === 'valid' ? 'Valid' :
                     visitTypeReasonValidation.status === 'connection-failed' ? 'CONNECTION FAILED' : 'Invalid'}
                    <span className="info-tooltip">
                      <span className="info-icon">ⓘ</span>
                      <span className="tooltip-text">
                        Visit Types must have a reason mapping in the phynd.VisitTypeReason table. Patients only see the Reasons and not the Visit Type names so a mapping must exist.
                      </span>
                    </span>
                  </span>
                  {visitTypeReasonValidation && visitTypeReasonValidation.status !== 'checking' && (
                    <div className="item-details">
                      {visitTypeReasonValidation.status === 'connection-failed' ? (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#D13438' }}>
                            ❌ Unable to connect to database. This validation requires network access to Azure SQL Database on port 1433.
                          </div>
                          {visitTypeReasonValidation.attemptedVisitTypeIds && visitTypeReasonValidation.attemptedVisitTypeIds.length > 0 && (
                            <>
                              <div style={{ marginTop: '1rem' }}>
                                <strong>Visit Types That Should Have Been Tested ({visitTypeReasonValidation.attemptedVisitTypeIds.length}):</strong>
                              </div>
                              <div className="slots-table-wrapper">
                                <table className="results-table validation-results-table">
                                  <thead>
                                    <tr>
                                      <th>Visit Type ID</th>
                                      <th>Visit Type Reason</th>
                                      <th>Type Of Provider</th>
                                      <th>For Accepts</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visitTypeReasonValidation.attemptedVisitTypeIds.map((vtId: string, idx: number) => {
                                      return (
                                        <tr key={idx} style={{ background: '#ffebee' }}>
                                          <td>{vtId}</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                          <td style={{ color: '#999' }}>Unable to validate</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </>
                      ) : visitTypeReasonValidation.status === 'invalid' ? (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                            {visitTypeReasonValidation.missingVisitTypes.length > 0 && (
                              <div style={{ color: '#D13438' }}>
                                ❌ Missing in database: {visitTypeReasonValidation.missingVisitTypes.join(', ')}
                              </div>
                            )}
                          </div>
                          {visitTypeReasonValidation.validMappings.length > 0 && (
                            <>
                              <div style={{ marginTop: '1rem' }}>
                                <strong>Visit Type Reason Mappings:</strong>
                              </div>
                              <div className="slots-table-wrapper">
                                <table className="results-table validation-results-table">
                                  <thead>
                                    <tr>
                                      <th className="sortable" onClick={() => handleVisitTypeReasonSort('VisitTypeId')}>
                                        Visit Type ID{getSortIcon('VisitTypeId', visitTypeReasonSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypeReasonSort('VisitTypeReason')}>
                                        Visit Type Reason{getSortIcon('VisitTypeReason', visitTypeReasonSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypeReasonSort('TypeOfProvider')}>
                                        Type Of Provider{getSortIcon('TypeOfProvider', visitTypeReasonSort)}
                                      </th>
                                      <th className="sortable" onClick={() => handleVisitTypeReasonSort('ForAccepts')}>
                                        For Accepts{getSortIcon('ForAccepts', visitTypeReasonSort)}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getSortedVisitTypeReasons().map((vtr: any, idx: number) => (
                                      <tr key={idx}>
                                        <td>{vtr.VisitTypeId}</td>
                                        <td>{vtr.VisitTypeReason || 'N/A'}</td>
                                        <td>{vtr.TypeOfProvider || 'N/A'}</td>
                                        <td>{vtr.ForAccepts || 'N/A'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#107C10' }}>
                            ✅ All {new Set(visitTypeReasonValidation.validMappings.map((m: any) => m.VisitTypeId)).size} visit types have valid reason mappings ({visitTypeReasonValidation.validMappings.length} total mappings)
                          </div>
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Visit Type Reason Mappings:</strong>
                          </div>
                          <div className="slots-table-wrapper">
                            <table className="results-table validation-results-table">
                              <thead>
                                <tr>
                                  <th className="sortable" onClick={() => handleVisitTypeReasonSort('VisitTypeId')}>
                                    Visit Type ID{getSortIcon('VisitTypeId', visitTypeReasonSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypeReasonSort('VisitTypeReason')}>
                                    Visit Type Reason{getSortIcon('VisitTypeReason', visitTypeReasonSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypeReasonSort('TypeOfProvider')}>
                                    Type Of Provider{getSortIcon('TypeOfProvider', visitTypeReasonSort)}
                                  </th>
                                  <th className="sortable" onClick={() => handleVisitTypeReasonSort('ForAccepts')}>
                                    For Accepts{getSortIcon('ForAccepts', visitTypeReasonSort)}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSortedVisitTypeReasons().map((vtr: any, idx: number) => (
                                  <tr key={idx}>
                                    <td>{vtr.VisitTypeId}</td>
                                    <td>{vtr.VisitTypeReason || 'N/A'}</td>
                                    <td>{vtr.TypeOfProvider || 'N/A'}</td>
                                    <td>{vtr.ForAccepts || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </label>
                {visitTypeReasonValidation && visitTypeReasonValidation.status === 'invalid' && (
                  <StepGuidance>
                    Patients pick a Reason rather than a visit type name, so a visit type with no reason mapping cannot be booked even when Epic offers slots for it.
                    <ul>
                      <li>Send the missing visit type IDs listed above to the <strong>Digital Scheduling</strong> team to have reason mappings added.</li>
                    </ul>
                  </StepGuidance>
                )}
                {visitTypeReasonValidation && visitTypeReasonValidation.status === 'connection-failed' && (
                  <StepGuidance>
                    This check could not reach the database, so it is unknown rather than failed. It needs network access to Azure SQL on port 1433 - retry from a connected network before treating the reasons as missing.
                  </StepGuidance>
                )}
              </div>

            </div>
          </div>
        </div>

        {npi && (
          <ProviderWorkQueuePanel
            npi={npi}
            data={workItems.data}
            loading={workItems.loading}
            error={workItems.error}
            reload={workItems.reload}
            open={workQueueOpen}
            onToggle={() => setWorkQueueChoice(!workQueueOpen)}
          />
        )}

        <div className="detail-grid">
          {(provider.Locations || provider.locations) && (provider.Locations || provider.locations)!.length > 0 && (
            <div className="detail-card full-width">
              <h3>Locations ({(provider.Locations || provider.locations)!.length})</h3>
              <div className="locations-list">
                {(provider.Locations || provider.locations)!.map((loc, idx) => (
                  <div key={idx} className="location-card">
                    <div className="location-info">
                      <h4>{loc.LocationName || loc.locationName || 'Unnamed Location'}</h4>
                      {(loc.LocationType || loc.locationType) && <p className="location-type">{loc.LocationType || loc.locationType}</p>}
                      <div className="location-address">
                        {(loc.LocationStreet1 || loc.locationStreet1) && <p>{loc.LocationStreet1 || loc.locationStreet1}</p>}
                        {(loc.LocationStreet2 || loc.locationStreet2) && <p>{loc.LocationStreet2 || loc.locationStreet2}</p>}
                        <p>
                          {(loc.LocationCity || loc.locationCity) && `${loc.LocationCity || loc.locationCity}, `}
                          {loc.LocationState || loc.locationState} {loc.LocationZip || loc.locationZip}
                        </p>
                      </div>
                      {(loc.LocationPhone || loc.locationPhone) && (
                        <p className="location-contact">
                          <strong>Phone:</strong> {loc.LocationPhone || loc.locationPhone}
                        </p>
                      )}
                      {(loc.EpicDepartmentId || loc.epicDepartmentId) && (
                        <p className="location-id">
                          <strong>Epic Department ID:</strong> {padEpicId(loc.EpicDepartmentId || loc.epicDepartmentId)}
                        </p>
                      )}
                      {(loc.LocationID || loc.locationID) && (
                        <p className="location-id">
                          <strong>Location ID:</strong> {loc.LocationID || loc.locationID}
                        </p>
                      )}
                    </div>

                    {(loc.OpenSchedules || loc.openSchedules) && (
                      (() => {
                        const schedule = loc.OpenSchedules || loc.openSchedules;
                        const calendarSlots = buildAvailabilitySlots(
                          schedule,
                          loc.LocationName || loc.locationName,
                        );
                        if (calendarSlots.length === 0) return null;

                        return (
                          <div className="location-schedule">
                            <h5>Available Appointments</h5>
                            <AvailabilityCalendar slots={calendarSlots} />
                          </div>
                        );
                      })()
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(provider.Insurance || provider.insurance) && (provider.Insurance || provider.insurance)!.length > 0 && (() => {
            // Group insurance by carrier
            const insuranceList = provider.Insurance || provider.insurance || [];
            const groupedInsurance = insuranceList.reduce((acc: Record<string, string[]>, ins) => {
              const carrier = ins.InsurancePlanCarrier || ins.insurancePlanCarrier || 'Unknown Carrier';
              const product = ins.InsuranceProduct || ins.insuranceProduct;

              if (!acc[carrier]) {
                acc[carrier] = [];
              }
              if (product) {
                acc[carrier].push(product);
              }
              return acc;
            }, {});

            return (
              <div className="detail-card full-width">
                <h3>Insurance Accepted ({insuranceList.length})</h3>
                <div className="insurance-tree">
                  {Object.entries(groupedInsurance).map(([carrier, products], idx) => (
                    <InsuranceTreeNode key={idx} carrier={carrier} products={products} />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Full JSON Response from Validation */}
          {validationResult?.appointmentData && (
            <div className="detail-card full-width">
              <h3>Validation API Response (Step 3 - Epic Slots)</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowValidationResponse(current => !current)}
              >
                {showValidationResponse ? 'Hide Full Response' : 'Show Full Response'}
              </button>
              {showValidationResponse && (
                <div className="json-viewer-wrapper">
                  <JsonView value={validationResult.appointmentData} collapsed={2} />
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProviderDetail;
